import { CachedServicesClient } from "./CachedServicesClient";
import { BaseServicesClientAuthError } from "./base-services-client";
import { isSSR } from "../helpers/client-server-helper";
import {
  AVAILABILITY_SCOPES,
  PRODUCT_SCOPES,
  DISCO_DRIVERS,
  membershipLens,
  DISCO_CATALOG_PRODUCT_SCOPES,
} from "rtr-constants";
import HTTP_HEADERS from "rtr-constants/http-headers";
import { ProductImageSize } from "helpers/product-image-helpers";
import { SFLogger } from "logger/logger";
import $ from "clients/RawClient";

const logger = SFLogger("disco-search-service-client");

const DEFAULT_HEADERS = {
  "Accept": "application/json",
  "Content-Type": "application/json",
};

// based on the response shape from the API
const ERROR_TEMPLATE = {
  items: [],
  count: 0,
  filters: {},
  source: "error",
};

const discoHost = (() => {
  if (typeof process !== "undefined" && process.env) {
    return isSSR() ? process.env.CONFIG__DISCO__HOST : process.env.NEXT_PUBLIC_CONFIG__DISCO__HOST;
  } else {
    return "";
  }
})();

const version = "/v0";
const versionV2 = "/v2";

/**
 *
 * @param {object} query - The initial search query object to format
 * @param {number} page - The page number for pagination
 * @param {number} limit - The number of items per page
 * @param {boolean} isActiveMember - Indicates if the user is an active member
 * @param {@import("components/types/availability-scope").AvailabilityScope} availabilityScope - The availability scope (e.g., "reserve", "membership")
 * @returns {URLSearchParams} - The formatted URLSearchParams object
 */

export const formatQuery = (query, page, limit, isActiveMember, availabilityScope) => {
  const queryParams = new URLSearchParams(query);
  queryParams.set("itemOffset", (page > 1 ? page - 1 : 0) * limit);
  queryParams.set("itemLimit", limit);
  // only set sortOptions if it exists in the query. Otherwise, it will default to the API's default sort order.
  if (queryParams.has("sortOptions")) {
    queryParams.set("sortOptions", query.sortOptions.toUpperCase());
  }

  if (!queryParams.has("availableOnly")) {
    queryParams.set("availableOnly", true);
  }

  if (isActiveMember && availabilityScope === "reserve") {
    queryParams.set("isActiveMember", isActiveMember);
  }
  return queryParams;
};

export class DiscoSearchServiceClient extends CachedServicesClient {
  static CONFIG_HOST = discoHost;

  async clearanceSearch(query, page = 0, limit = 60) {
    const queryParams = new URLSearchParams(query);

    queryParams.set("itemOffset", (page > 1 ? page - 1 : 0) * limit);
    queryParams.set("itemLimit", limit);
    queryParams.set("sortOptions", query.sortOptions ? query.sortOptions.toUpperCase() : "NEWEST");

    if (!queryParams.has("availableOnly")) {
      queryParams.set("availableOnly", true);
    }

    return this.#handleRequest(`${version}/clearance/products/search?${queryParams}`);
  }

  async clearanceByCategoryId(query, page = 0, limit = 40) {
    const { categoryId, sortOptions } = query;
    const queryParams = new URLSearchParams();

    queryParams.set("itemOffset", (page > 1 ? page - 1 : 0) * limit);
    queryParams.set("itemLimit", limit);
    queryParams.set("sortOptions", sortOptions ? sortOptions.toUpperCase() : "NEWEST");

    if (!queryParams.has("availableOnly")) {
      queryParams.set("availableOnly", true);
    }

    return this.#handleRequest(`${version}/clearance/products/category/${categoryId}?${queryParams}`);
  }

  async curationSearch(query, page = 0, limit = 60, lens = null) {
    const queryParams = new URLSearchParams(query);

    queryParams.set("itemOffset", (page > 1 ? page - 1 : 0) * limit);
    queryParams.set("itemLimit", limit);
    queryParams.set("sortOptions", query.sortOptions ? query.sortOptions.toUpperCase() : "NEWEST");

    if (lens === "classic") {
      return this.#handleReserveRequest(queryParams);
    }

    // Only need the first curation as a path param (below)
    queryParams.delete("curationId");

    const path = `${version}/membership/products/curation/${query.curationId[0]}?${queryParams}`;

    if (isSSR()) {
      logger.info(`curationSearch, path=${path}`);
    }

    return this.#handleRequest(path);
  }

  async designerSearch(query, page = 0, limit = 60, lens = null) {
    const queryParams = new URLSearchParams(query);

    queryParams.set("itemOffset", (page > 1 ? page - 1 : 0) * limit);
    queryParams.set("itemLimit", limit);
    queryParams.set("sortOptions", query.sortOptions ? query.sortOptions.toUpperCase() : "NEWEST");

    if (lens === "classic") {
      return this.#handleReserveRequest(queryParams);
    }

    if (!queryParams.has("availableOnly")) {
      queryParams.set("availableOnly", true);
    }

    // Only need the first designer as a path param (below)
    queryParams.delete("designerId");

    // The designer endpoint doesn't support curations
    queryParams.delete("curationId");

    return this.#handleRequest(`${version}/membership/products/designer/${query.designerId[0]}?${queryParams}`);
  }

  async categorySearch(query, page = 0, limit = 60, lens) {
    const queryParams = new URLSearchParams(query);

    queryParams.set("itemOffset", (page > 1 ? page - 1 : 0) * limit);

    queryParams.set("itemLimit", limit);
    queryParams.set("sortOptions", query.sortOptions ? query.sortOptions.toUpperCase() : "NEWEST");

    if (lens === "classic") {
      return this.#handleReserveRequest(queryParams);
    }

    if (!queryParams.has("availableOnly")) {
      queryParams.set("availableOnly", true);
    }

    queryParams.delete("category");

    return this.#handleRequest(`${version}/membership/products/category/${query.category[0]}?${queryParams}`);
  }

  async viewAllSearch(query, page = 0, limit = 60, lens) {
    const queryParams = new URLSearchParams(query);

    queryParams.set("itemOffset", (page > 1 ? page - 1 : 0) * limit);
    queryParams.set("itemLimit", limit);
    queryParams.set("sortOptions", query.sortOptions ? query.sortOptions.toUpperCase() : "NEWEST");

    if (!queryParams.has("availableOnly") && lens !== "classic") {
      queryParams.set("availableOnly", true);
    }

    const rootCategories = ["WomensApparel", "WomensAccessories"];
    const categories = queryParams.get("category")?.split(",");

    /**
     * We only send the root categories if there are no subcategories
     * This is because if root categories are included the api will return EVERYTHING
     */
    const subCategories = categories?.filter(cat => !rootCategories.includes(cat));
    if (subCategories?.length > 0) {
      queryParams.set(
        "category",
        categories.filter(cat => !rootCategories.includes(cat))
      );
    }

    const prefix = lens === "classic" ? "reserve" : "membership";

    const path = `${version}/${prefix}/products/search?${queryParams}`;

    if (isSSR()) {
      logger.info(`viewAllSearch, path=${path}`);
    }

    return this.#handleRequest(path);
  }

  /**
   * Search full catalog of published items, regardless of current availability.
   * @param {object} query
   * @param {number} page
   * @param {number} limit
   * @param {import("components/types/disco-search-type").DiscoCatalogDriver} catalogDriver
   * @returns
   */
  async availabilityAgnosticSearch(query, page = 0, limit = 60, catalogDriver = DISCO_CATALOG_PRODUCT_SCOPES.DESIGNER) {
    const queryParams = new URLSearchParams(query);

    queryParams.set("itemOffset", (page > 1 ? page - 1 : 0) * limit);
    queryParams.set("itemLimit", limit);

    const catalogDriverParam = catalogDriver + "Id";
    queryParams.delete(catalogDriverParam);

    const path = `${version}/explore/products/${catalogDriver}/${query[catalogDriverParam][0]}?${queryParams}`;

    if (isSSR()) {
      logger.info(`exploreSearch, path=${path}`);
    }

    return this.#handleRequest(path);
  }

  async shortlist(query, page = 0, limit = 60, availabilityScope = AVAILABILITY_SCOPES.MEMBERSHIP, userId = "") {
    const queryParams = new URLSearchParams(query);

    queryParams.set("itemOffset", (page > 1 ? page - 1 : 0) * limit);
    queryParams.set("itemLimit", limit);

    if (query.sortOptions) {
      queryParams.set("sortOptions", query.sortOptions.toUpperCase());
    }

    const rootCategories = ["WomensApparel", "WomensAccessories"];
    const categories = queryParams.get("category")?.split(",");

    /**
     * We only send the root categories if there are no subcategories
     * This is because if root categories are included the api will return EVERYTHING
     */
    const subCategories = categories?.filter(cat => !rootCategories.includes(cat));
    if (subCategories?.length > 0) {
      queryParams.set(
        "category",
        categories.filter(cat => !rootCategories.includes(cat))
      );
    }

    const path = `${versionV2}/${availabilityScope}/products/${DISCO_DRIVERS.USER}/${PRODUCT_SCOPES.SHORTLIST}?${queryParams}`;

    if (isSSR()) {
      logger.info(`viewAllSearch, path=${path}`);
    }

    const additionalHeaders = {
      [HTTP_HEADERS.X_RTR_USER_ID]: userId?.length > 0 ? userId : void 0,
    };

    return this.#handleRequest(path, additionalHeaders);
  }

  async typeaheadSearch(term) {
    const version = "/v1";
    const path = `${version}/typeahead?searchPrompt=${term}`;

    return this.get(path, null, {
      headers: DEFAULT_HEADERS,
      bypassCache: true,
    })
      .then(async response => {
        return await this.returnJsonIfSuccessful(response);
      })
      .catch(e => {
        window?.Sentry?.captureException(`Error getting typeahead search data: ${e}`);
        console.error(`Error getting typeahead search data`, e);
        return { ...ERROR_TEMPLATE, message: e.message };
      });
  }

  async productsDataByStyleNames(styleNames) {
    let productsData;
    try {
      const res = await this.get(
        `${version}/products`,
        {
          ids: styleNames.join(","),
        },
        {
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      productsData = await this.returnJsonIfSuccessful(res);
    } catch (e) {
      this.reportError(e);
      throw new Error("Error getting product data from disco:", e);
    }

    // make sure images URLs have the x1080 paths
    this.changeImageResolution(productsData);
    return productsData;
  }

  async getProductsDataByPdpUrl(url) {
    let productsData;
    try {
      const res = await this.get(
        `${version}/products`,
        {
          urlPath: url,
        },
        {
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      productsData = await this.returnJsonIfSuccessful(res);
    } catch (e) {
      this.reportError(e);
      return {};
    }
    // make sure images URLs have the x1080 paths
    this.changeImageResolution(productsData);
    return productsData;
  }

  changeImageResolution(productsData) {
    productsData?.items?.forEach(product => {
      product.images = (Array.isArray(product?.images) && product.images.length > 0 ? product.images : [])?.map(
        imageUrl => {
          return imageUrl?.replace(ProductImageSize.x270.path, ProductImageSize.x1080.path);
        }
      );
    });
  }

  async recommendationSearch(query, page = 0, limit = 60) {
    const queryParams = new URLSearchParams(query);
    queryParams.set("itemOffset", (page > 1 ? page - 1 : 0) * limit);
    queryParams.set("itemLimit", limit);
    queryParams.set("sortOptions", query.sortOptions ? query.sortOptions.toUpperCase() : "NEWEST");

    const path = `v0/recommendation/catalog/filter?${queryParams}`;

    return this.#handleRequest(path);
  }

  async similarDesignersSearch(query, page = 0, limit = 60, lens = "membership") {
    const queryParams = new URLSearchParams(query);
    queryParams.set("itemOffset", (page > 1 ? page - 1 : 0) * limit);
    queryParams.set("itemLimit", limit);
    queryParams.set("sortOptions", query.sortOptions ? query.sortOptions.toUpperCase() : "NEWEST");

    const path = `v2/${lens}/recommendation/catalog/similar_designers?${queryParams}`;
    return this.#handleRequest(path);
  }

  async membershipAvailabilitySingleProduct(query) {
    const productId = query.productId;
    delete query.productId;

    const queryParams = new URLSearchParams(query);

    return this.#handleRequest(`${version}/membership/availability/${productId}?${queryParams}`);
  }

  /**
   * @typedef {object} CatalogSearchParams
   * @property {object} query - The search query object.
   * @property {number} [page=0] - The page number for pagination.
   * @property {number} [limit=60] - The number of items per page.
   * @property {import("components/types/disco-search-type").MembershipLensType} lens - The lens type (e.g., "classic", "membership").
   * @property {import("components/types/disco-search-type").DiscoCatalogProductScopeType} productScope - The product scope (e.g., "category", "curation", "designer", "search").
   * @property {import("components/types/disco-search-type").AvailabilityScopeType} [availabilityScope] - The availability scope (e.g., "reserve", "membership") (optional).
   * @property {boolean} [isActiveMember] - Indicates if the user is an active member (optional).
   */
  /**
   * Catalog search endpoint
   * @param {CatalogSearchParams} params - The parameters for catalog search.
   * @returns {Promise<any>} - A promise that resolves to the search results.
   */
  async catalogSearch({
    query,
    page = 0,
    limit = 60,
    lens,
    availabilityScope: availabilityScopeFromParams,
    productScope,
    isActiveMember,
  }) {
    if (!Object.values(DISCO_CATALOG_PRODUCT_SCOPES).includes(productScope)) {
      throw new Error("Catalog searches must specify a valid productScope");
    }

    const queryParams = new URLSearchParams(query);
    queryParams.set("itemOffset", (page > 1 ? page - 1 : 0) * limit);
    queryParams.set("itemLimit", limit);

    if (queryParams.has("sortOptions")) {
      queryParams.set("sortOptions", query.sortOptions.toUpperCase());
    }

    const availabilityScope = availabilityScopeFromParams || (lens === "classic" ? "reserve" : "membership");

    if (isActiveMember && availabilityScope === "reserve") {
      queryParams.set("isActiveMember", isActiveMember);
    }

    return this.#handleRequest(
      `${versionV2}/${availabilityScope}/products/${DISCO_DRIVERS.CATALOG}/${productScope}?${queryParams}`
    );
  }

  /**
   * @typedef {object} V2UserDriverSearchBaseParam
   * @property {object} [query] - The search query object.
   * @property {number} [page=0] - The page number for pagination.
   * @property {number} [limit=60] - The number of items per page.
   * @property {import("components/types/disco-search-type").MembershipLensType} [lens] - The lens type (e.g., "classic", "membership").
   * @property {string} userId - The user ID for personalized searches.
   * @property {import("components/types/disco-search-type").ProductScopeType} productScope - The product scope (e.g., "recently_viewed", "shortlist").
   * @property {boolean} [isActiveMember] - Indicates if the user is an active member (optional).
   * @property {Object} [headers] - Additional headers to include in the request (optional).
   * @property {URLSearchParams} [formattedUrlSearchQuery] - If present, the query is already formatted and we don't need to format again.
   * @property {import("components/types/disco-search-type").AvailabilityScopeType} [availabilityScope] - The availability scope (e.g., "reserve", "membership") (optional).
   */
  /**
   * V2 user product search endpoint
   * @param {V2UserDriverSearchBaseParam} baseParam - The base parameters for V2 user driver search.
   * @param {import("components/types/disco-search-type").DiscoV2SearchType} searchType - The type of search ("products" or "recommendation").
   * @returns {Promise<any>} - A promise that resolves to the search results.
   */
  async v2UserDriverSearch(
    {
      query,
      page = 0,
      limit = 60,
      lens,
      userId,
      productScope,
      isActiveMember,
      headers = {},
      formattedUrlSearchQuery,
      availabilityScope: availabilityScopeFromParams,
    },
    searchType
  ) {
    if (!userId || !productScope) {
      throw new Error("userId and productScope are required for v2UserDriverSearch");
    }
    const availabilityScope =
      availabilityScopeFromParams ||
      (lens === membershipLens.classic ? AVAILABILITY_SCOPES.RESERVE : AVAILABILITY_SCOPES.MEMBERSHIP);
    const queryParams = formattedUrlSearchQuery || formatQuery(query, page, limit, isActiveMember, availabilityScope);

    if (isSSR()) {
      return this.#handleRequest(
        `${versionV2}/${availabilityScope}/${searchType}/${
          DISCO_DRIVERS.USER
        }/${productScope}?${queryParams.toString()}`,
        headers
      );
    } else {
      return $.get({
        url: `/api/disco-search/v2/user-driver-search`,
        data: {
          availabilityScope,
          productScope,
          searchType,
          formattedUrlSearchQueryString: queryParams.toString(),
        },
      });
    }
  }

  /**
   * V2 user product search endpoint
   * @param {V2UserDriverSearchBaseParam} params - The parameters for V2 user driver search.
   * @returns {Promise<any>} - A promise that resolves to the search results.
   */
  async userProductSearch(params) {
    return this.v2UserDriverSearch({ ...params }, "products");
  }

  /**   * V2 user recommendation search endpoint
   * @param {V2UserDriverSearchBaseParam} params - The parameters for V2 recommendation product search.
   * @returns {Promise<any>} - A promise that resolves to the search results.
   */
  async recommendationProductSearch(params) {
    return this.v2UserDriverSearch({ ...params }, "recommendation");
  }

  async userShortlistSummary(userId, headers) {
    if (!userId) {
      throw new Error("userId is required for userShortlistSummary");
    }

    let response;

    if (isSSR()) {
      response = await this.#handleRequest("v0/shortlists/user", headers);
    } else {
      response = await $.get({
        url: "/api/disco-search/v0/shortlists/user",
      });
    }

    return response?.filter?.(shortlist => shortlist.shortlistId !== "DOWNVOTES");
  }

  async designers() {
    const path = "v1/designers";

    if (isSSR()) {
      logger.info(`designers, path=${path}`);
    }

    return this.#handleRequest(path);
  }

  /**
   * sbenedict 5/25 - Reserve currently uses a generic endpoint whereas membership uses dedicated use-case based ones.
   */
  #handleReserveRequest = async queryParams => {
    const path = `${version}/reserve/products/search?${queryParams}`;

    return this.#handleRequest(path);
  };

  #handleRequest = async (path, additionalHeaders = {}) => {
    return this.get(path, null, {
      headers: {
        ...DEFAULT_HEADERS,
        ...additionalHeaders,
      },
      bypassCache: true,
    })
      .then(async response => {
        return await this.returnJsonIfSuccessful(response);
      })
      .catch(e => {
        if (isSSR() && e instanceof BaseServicesClientAuthError) {
          // rethrow auth errors on the server so that they can be handled upstream
          throw e;
        }
        this.reportError(e);
        return { ...ERROR_TEMPLATE, message: e.message };
      });
  };

  async buyAvailabilitySingleProduct(query) {
    const productId = query.productId;
    delete query.productId;

    const queryParams = new URLSearchParams(query);
    return this.get(`${version}/buy/availability/${productId}?${queryParams}`, null, {
      headers: DEFAULT_HEADERS,
      bypassCache: true,
    })
      .then(async response => {
        return await this.returnJsonIfSuccessful(response);
      })
      .catch(e => {
        this.reportError(e);
        return { ...ERROR_TEMPLATE, message: e.message };
      });
  }

  async buyPricingSingleProduct(query) {
    const productId = query.productId;
    return this.get(`${version}/buy/pricing/${productId}`, null, {
      headers: DEFAULT_HEADERS,
      bypassCache: true,
    })
      .then(async response => {
        return await this.returnJsonIfSuccessful(response);
      })
      .catch(e => {
        this.reportError(e);
        return { ...ERROR_TEMPLATE, message: e.message };
      });
  }

  async clearanceAvailabilitySingleProduct(query) {
    const productId = query.productId;
    delete query.productId;

    const queryParams = new URLSearchParams(query);

    return this.get(`${version}/clearance/availability/${productId}?${queryParams}`, null, {
      headers: DEFAULT_HEADERS,
      bypassCache: true,
    })
      .then(async response => {
        return await this.returnJsonIfSuccessful(response);
      })
      .catch(e => {
        this.reportError(e);
        return { ...ERROR_TEMPLATE, message: e.message };
      });
  }

  async clearancePricingSingleProduct(query) {
    const productId = query.productId;
    delete query.productId;

    const queryParams = new URLSearchParams(query);
    return this.get(`${version}/clearance/pricing/${productId}?${queryParams}`, null, {
      headers: DEFAULT_HEADERS,
      bypassCache: true,
    })
      .then(async response => {
        return await this.returnJsonIfSuccessful(response);
      })
      .catch(e => {
        this.reportError(e);
        return { ...ERROR_TEMPLATE, message: e.message };
      });
  }

  async reserveAvailabilitySingleProduct(query) {
    const productId = query.productId;
    delete query.productId;

    const queryParams = new URLSearchParams(query);

    return this.get(`${version}/reserve/availability/${productId}?${queryParams}`, null, {
      headers: DEFAULT_HEADERS,
      bypassCache: true,
    })
      .then(async response => {
        return await this.returnJsonIfSuccessful(response);
      })
      .catch(e => {
        this.reportError(e);
        return { ...ERROR_TEMPLATE, message: e.message };
      });
  }

  async reservePricingSingleProduct(query) {
    const productId = query.productId;
    delete query.productId;

    const queryParams = new URLSearchParams(query);
    return this.get(`${version}/reserve/pricing/${productId}?${queryParams}`, null, {
      headers: DEFAULT_HEADERS,
      bypassCache: true,
    })
      .then(async response => await this.returnJsonIfSuccessful(response))
      .catch(e => {
        this.reportError(e);
        return { ...ERROR_TEMPLATE, message: e.message };
      });
  }

  async getSizeGuideByProductId(productId) {
    const path = `${version}/sizeguide/${productId}`;

    return this.get(path, null, {
      headers: DEFAULT_HEADERS,
      bypassCache: true,
    })
      .then(async response => {
        return await this.returnJsonIfSuccessful(response);
      })
      .catch(e => {
        window?.Sentry?.captureException(`Error getting size guide data: ${e}`);
        console.error(`Error getting size guide data`, e);
        return { ...ERROR_TEMPLATE, message: e.message };
      });
  }

  reportError = error => {
    if (isSSR()) {
      console.error(error);
    } else if (window?.Sentry && window?.Sentry?.captureException) {
      window.Sentry.captureException(error);
    }
  };

  async returnJsonIfSuccessful(res) {
    const text = await res.text();
    if (res.status === 200 || res.status === 204) {
      return JSON.parse(text || "{}");
    } else if (isSSR()) {
      if (res.status === 403 || res.status === 401) {
        throw new BaseServicesClientAuthError(`Auth Error from DISCO: ${res.status} ${text}`);
      }
      console.error("Non-200 or Empty response from endpoint", res.status, text);
    } else {
      throw new Error(`Non-200 or Empty response from endpoint: ${res.status} ${text}`);
    }
  }
}
